import { Box, Text, theme } from '@everlywell/ui-kit';
import parse from 'html-react-parser';
import React, { useCallback } from 'react';
import { sanitize } from 'utils/domHelpers';

export type InfoCardV2Props = {
  backgroundColor?: string;
  text: {
    content: string | React.ReactNode;
    color?: string;
  };
  footnote?: {
    content: string;
    color?: string;
  };
  highlightTextColor?: string;
  cta?: React.ReactNode;
  desktopWidth?: 'md' | 'lg';
};

const InfoCardV2 = ({
  backgroundColor = theme.colors.viridian.dark,
  text,
  highlightTextColor = theme.colors.whiteCS.base,
  footnote,
  cta = null,
  desktopWidth,
}: InfoCardV2Props) => {
  const setDesktopWidth = useCallback(() => {
    const widths = {
      md: '280px',
      lg: '664px',
    };

    if (desktopWidth === undefined) return 'unset';

    return widths[desktopWidth];
  }, [desktopWidth]);

  return (
    <Box
      position="relative"
      padding={6}
      backgroundColor={backgroundColor}
      borderRadius="xl"
      overflow="hidden"
      height="100%"
      minHeight={['200px', '400px']}
      width={['100%', setDesktopWidth()]}
      display="flex"
      flexDirection="column"
      gap="40px"
    >
      <Box
        fontFamily="body"
        fontSize={['3xl', '5xl']}
        lineHeight={['shorter']}
        fontWeight="medium"
        line-height="shorter"
        color={text?.color}
        sx={{
          'strong, b': {
            fontWeight: '500',
            color: highlightTextColor,
          },
        }}
      >
        {typeof text?.content === 'string'
          ? parse(sanitize(text?.content))
          : text?.content}
      </Box>

      <Box marginTop="auto" display="flex" alignItems="center">
        {footnote && (
          <Text
            fontSize={['md', 'lg']}
            opacity="0.6"
            color={footnote?.color}
            flex="1"
          >
            {footnote?.content}
          </Text>
        )}
        {cta && (
          <Box
            marginLeft="auto"
            display="flex"
            alignItems="center"
            flex="1"
            justifyContent="end"
          >
            {cta}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default InfoCardV2;
